import React from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import keys from 'lodash/keys';
import { MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AppStoreState, SETTING_TYPES } from 'types';
import { saveLastProductUsed } from 'util/local-storage-util';
import { fetchUserClassrooms } from 'state/modules/entity/custom/classrooms/thunks';
import {
  fetchTeachingOrder,
  getProductConfiguration
} from 'state/modules/entity/thunks/teaching-order';
import { fetchLessonPlan } from 'state/modules/entity/thunks/lesson-planner';
import { fetchSupportResourcesForProduct } from 'state/modules/entity/custom/resource-favorites-mutations/thunks/resource-thunks';
import { getUserAssignmentsView } from 'state/modules/entity/thunks/assignment';
import { remCalc } from '../../../util/style-util';
import {
  getNewLink,
  getSelectedWorkbook,
  getStudentWorkbookCodeForNewLocale
} from '../../../util/workbook-util';
import { changeAppLocale, selectWorkbook } from '../../../state/modules/app';
import { getUsersWorkbooks } from '../../../state/modules/entity/thunks';
import { getTours } from '../../../state/modules/tour';
import { saveUserSetting } from '../../../state/modules/user/thunks';
import {
  getLocale,
  localeToLanguage,
  setLocale,
  translate
} from '../../../translations/translator';
import StyledBaseInput from '../Inputs/StyledBaseInput';

const translateHome = key => translate('home', key);

const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.substr(1);
};

type Props = {
  labelFontColor?: string;
  width?: string;
};
const LanguageDropdown: React.FC<Props> = ({ labelFontColor = 'black', width }) => {
  const { workbookDropdown, workbookDropdownItem } = useStyles();
  const user = useSelector(({ user }: AppStoreState) => user);
  const currentWorkbook = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const classrooms: any = useSelector(({ entity }: AppStoreState) => entity.classrooms);
  const dispatch = useDispatch() as any;
  const navigate = useNavigate();

  const replaceNavigate = (url: string) => {
    navigate(url, { replace: true });
  };

  const maybeGetAssignmentsView = async () => {
    if (user.isAuditor) return;
    if (!classrooms.length) return;
    await dispatch(getUserAssignmentsView());
  };

  const handleLessonPlannerLocaleChange = async () => {
    await batch(async () => {
      await dispatch(fetchUserClassrooms());
      await dispatch(fetchLessonPlan());
      await maybeGetAssignmentsView();
      await dispatch(fetchTeachingOrder());
      await dispatch(getProductConfiguration());
    });
  };

  const handleSupportLocaleChange = async () => {
    await dispatch(
      fetchSupportResourcesForProduct(+currentWorkbook.products[0].productCurriculumId)
    );
  };

  const handleAssignmentTrackerLocaleChange = async () => {
    await dispatch(fetchUserClassrooms());
  };

  const selectLocale = async (newLocale: string) => {
    setLocale(newLocale);
    batch(() => {
      dispatch(changeAppLocale(newLocale));
      dispatch(saveUserSetting(SETTING_TYPES.LOCALE, newLocale));
      dispatch(getTours());
    });

    //optimisticBooks because allBooks is not updated from the locale change, so
    //we need to capture the output
    const optimisticBooks = await dispatch(getUsersWorkbooks());
    const newBookCode = getStudentWorkbookCodeForNewLocale(
      optimisticBooks,
      currentWorkbook.products[0].productCurriculumId,
      newLocale
    );

    dispatch(selectWorkbook(getSelectedWorkbook(optimisticBooks, newBookCode)));
    const userId = user.me ? user.me.id : 0;

    saveLastProductUsed(userId, newBookCode, newLocale);
    //TODO: Change as part of routing. This logic allow for when you have the left nav
    //open and you change the language at the bottom of the left nav, that you back
    //to the same section/app
    if (window.location.pathname.includes('/home')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'home'));
    } else if (window.location.pathname.includes('/clickaways')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'clickAways'));
    } else if (window.location.pathname.includes('/pinned-clickaways')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'pinnedClickaways'));
    } else if (window.location.pathname.includes('/resources')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'resources'));
    } else if (window.location.pathname.includes('/manage-options')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'manageOptions'));
      handleLessonPlannerLocaleChange();
    } else if (window.location.pathname.includes('/lesson-planner')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'lessonPlanner'));
      handleLessonPlannerLocaleChange();
    } else if (window.location.pathname.includes('/assignment-tracker')) {
      handleAssignmentTrackerLocaleChange();
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'assignmentTracker'));
    } else if (window.location.pathname.includes('/books/TG')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'teachersGuide', userId));
    } else if (window.location.pathname.includes('/wet-dry-try')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'wdt'));
    } else if (window.location.pathname.includes('/letter-number-formations')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'lnp'));
    } else if (window.location.pathname.includes('/wood-pieces')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'wp'));
    } else if (window.location.pathname.includes('/support')) {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'support'));
      handleSupportLocaleChange();
    } else {
      replaceNavigate(getNewLink(newBookCode, currentWorkbook.edition, 'workbook', userId));
    }
  };

  return (
    <FormControl className={workbookDropdown} sx={{ margin: 'auto', width }} variant="standard">
      <InputLabel
        id="languageLabel"
        style={{ fontSize: '15px', fontWeight: 400, color: labelFontColor, marginLeft: remCalc(3) }}
      >
        {translateHome('changeLanguage')}
      </InputLabel>
      <Select
        labelId="languageLabel"
        className={workbookDropdown}
        value={getLocale()}
        onChange={(event: any) => selectLocale(event.target.value)}
        input={<StyledBaseInput />}
      >
        {keys(localeToLanguage).map(locale => (
          <MenuItem key={locale} value={locale} className={workbookDropdownItem}>
            {capitalize(localeToLanguage[locale])}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const useStyles = makeStyles(() => ({
  workbookDropdown: {
    borderRadius: '8px',
    fontWeight: 300,
    fontSize: '16px',
    width: 200,
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF'
    },
    '&:active': {
      backgroundColor: '#FFFFFF'
    },
    '&.MuiSelect-select': {
      backgroundColor: '#FFFFFF',
      '&:focus': {
        backgroundColor: '#FFFFFF'
      }
    }
  },
  workbookDropdownItem: {
    marginTop: remCalc(2),
    fontWeight: 300,
    padding: 0,
    fontSize: '16px'
  }
}));

export default LanguageDropdown;
